import type { GradeEntryType, PersonalizationType } from '@aeris/contracts'
import { UNIFORM_SIZES, type UniformSize } from '@aeris/uniforms'

/**
 * A grade da tela e a grade do banco — F170.
 *
 * O banco guarda uma linha por tamanho (é o que deixa somar e filtrar); a tela
 * desenha a planilha da loja, uma linha por peça. Estas funções são a ida e a
 * volta, fora do componente para que a ficha e o papel usem as mesmas.
 */

export type GradeRowDraft = {
  readonly key: string
  readonly block: string
  readonly piece: string
  readonly quantities: Readonly<Partial<Record<UniformSize, string>>>
}

let rowSequence = 0
export const nextKey = (): string => {
  rowSequence += 1
  return `linha-${String(rowSequence)}`
}

/** A grade gravada, remontada em linhas da planilha. */
export const draftFromEntries = (
  entries: readonly GradeEntryType[]
): GradeRowDraft[] => {
  const rows = new Map<number, GradeRowDraft>()
  for (const entry of [...entries].sort((a, b) => a.position - b.position)) {
    const row = rows.get(entry.position) ?? {
      key: nextKey(),
      block: entry.block,
      piece: entry.piece,
      quantities: {},
    }
    rows.set(entry.position, {
      ...row,
      quantities: { ...row.quantities, [entry.size]: String(entry.quantity) },
    })
  }
  return [...rows.values()]
}

export const quantityOf = (text: string | undefined): number => {
  if (text === undefined || text.trim() === '') return 0
  const value = Number(text)
  return Number.isSafeInteger(value) && value > 0 ? value : 0
}

/** As linhas da planilha, desmontadas no que o servidor grava. */
export const entriesFromDraft = (
  rows: readonly GradeRowDraft[]
): GradeEntryType[] =>
  rows.flatMap((row, position) =>
    UNIFORM_SIZES.flatMap((size) => {
      const quantity = quantityOf(row.quantities[size])
      return quantity === 0 ||
        row.piece.trim() === '' ||
        row.block.trim() === ''
        ? []
        : [
            {
              block: row.block.trim(),
              piece: row.piece.trim(),
              size,
              quantity,
              position,
            },
          ]
    })
  )

export const rowTotal = (row: GradeRowDraft): number =>
  UNIFORM_SIZES.reduce((sum, size) => sum + quantityOf(row.quantities[size]), 0)

export const gradeTotal = (rows: readonly GradeRowDraft[]): number =>
  rows.reduce((sum, row) => sum + rowTotal(row), 0)

/** Uma linha tem número mas falta o nome do modelo ou da peça. */
export const incompleteRows = (rows: readonly GradeRowDraft[]): number =>
  rows.filter(
    (row) =>
      rowTotal(row) > 0 && (row.block.trim() === '' || row.piece.trim() === '')
  ).length

export type PersonalizationDraft = PersonalizationType & {
  readonly key: string
}

export const draftFromPersonalization = (
  rows: readonly PersonalizationType[]
): PersonalizationDraft[] => rows.map((row) => ({ ...row, key: nextKey() }))

export const personalizationTotal = (
  rows: readonly PersonalizationType[]
): number => rows.reduce((sum, row) => sum + row.quantity, 0)
